// Runs list view. fn-1.29.
//
// **Acceptance**: "Runs list shows top-50 (paginated); each row links
// to `/runs/:id`."
//
// **Wiring**: `trpc.runs.list.useQuery({ limit: 50 })`. The control
// plane caps at 200; we default to 50. Pagination cursor is exposed
// via a "Load more" button — keeps the surface simple while still
// honoring the paginated upstream contract.

import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { StatusPill } from '../../layout/status-pill.js';
import { trpc } from '../../trpc-client.js';
import { formatTime } from '../format.js';

const PAGE_SIZE = 50;

export function RunsListPage(): React.ReactElement {
  const [cursors, setCursors] = useState<string[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const cursor = cursors[cursors.length - 1];
  const query = trpc.runs.list.useQuery({
    limit: PAGE_SIZE,
    ...(cursor !== undefined ? { cursor } : {}),
    ...(includeArchived ? { includeArchived: true } : {}),
  });
  const utils = trpc.useUtils();
  const archiveMutation = trpc.runs.archive.useMutation({
    onSuccess: () => {
      void utils.runs.list.invalidate();
    },
  });

  if (query.isPending) {
    return (
      <section className="page page-runs" aria-busy="true">
        <h1>Sessions</h1>
        <p>Loading sessions…</p>
      </section>
    );
  }
  if (query.error !== null) {
    return (
      <section className="page page-runs" role="alert">
        <h1>Sessions</h1>
        <p>Failed to load sessions: {query.error.message}</p>
      </section>
    );
  }

  const runs = query.data?.data ?? [];
  const nextCursor = query.data?.next_cursor ?? null;

  return (
    <section className="page page-runs">
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '1rem',
          marginBottom: '0.5rem',
        }}
      >
        <h1 style={{ margin: 0 }}>Sessions</h1>
        <QuickStartInline />
        <label
          className="sessions-include-archived-toggle"
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            color: 'var(--sb-text-muted)',
            fontSize: '0.82rem',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setCursors([]);
              setIncludeArchived(e.target.checked);
            }}
          />
          <span>Include archived</span>
        </label>
      </header>
      <p style={{ color: 'var(--sb-text-muted)', margin: '0.25rem 0 1rem' }}>
        Trace and debug agent runs against the platform.
      </p>
      {runs.length === 0 && cursors.length === 0 ? (
        <p style={{ color: 'var(--sb-text-muted)' }}>
          No sessions yet. Pick an agent above to start one, or visit the{' '}
          <Link to="/agents">Agents</Link> page for the full flow.
        </p>
      ) : (
        <table className="sb-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Matter</th>
              <th>Created</th>
              <th style={{ width: '7rem' }} aria-label="Row actions" />
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const archived = (run.metadata as { archived?: unknown } | null)?.archived === true;
              return (
                <tr key={run.id} style={archived ? { opacity: 0.55 } : undefined}>
                  <td className="sb-cell-id">
                    <Link to={`/runs/${encodeURIComponent(run.id)}`}>{run.id.slice(0, 12)}…</Link>
                    {archived && (
                      <span
                        className="sb-chip"
                        style={{ marginLeft: '0.4rem', fontSize: '0.68rem' }}
                        title="Archived — hidden from default list view"
                      >
                        archived
                      </span>
                    )}
                  </td>
                  <td>
                    <StatusPill status={run.status} />
                  </td>
                  <td className="sb-cell-mono">
                    {run.matter_id !== null ? `${run.matter_id.slice(0, 12)}…` : '—'}
                  </td>
                  <td style={{ color: 'var(--sb-text-muted)', fontSize: '0.85rem' }}>
                    {formatTime(run.created_at)}
                  </td>
                  <td>
                    {!archived && (
                      <button
                        type="button"
                        className="runs-list-archive"
                        onClick={() => void archiveMutation.mutateAsync({ sessionId: run.id })}
                        disabled={archiveMutation.isPending}
                        style={{
                          padding: '0.25rem 0.55rem',
                          fontSize: '0.75rem',
                          border: '1px solid var(--sb-border)',
                          borderRadius: '4px',
                          background: 'transparent',
                          color: 'var(--sb-text-muted)',
                          cursor: 'pointer',
                        }}
                        title="Soft-archive this session (hides from default list)"
                      >
                        Archive
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div
        className="runs-list-pager"
        style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}
      >
        {cursors.length > 0 && (
          <button
            type="button"
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
            className="runs-list-prev"
          >
            Previous
          </button>
        )}
        {nextCursor !== null && nextCursor.length > 0 && (
          <Fragment>
            <button
              type="button"
              onClick={() => setCursors((prev) => [...prev, nextCursor])}
              className="runs-list-next"
            >
              Load more
            </button>
          </Fragment>
        )}
      </div>
    </section>
  );
}

/**
 * fn-2.x — inline "Start a session" widget on the Sessions list. The
 * full Start-with-context flow lives on /agents (matter id, context
 * paste textarea, etc.); this gives a one-click path from the Sessions
 * tab when the operator doesn't need either. Picks an agent from the
 * existing agents.list query and posts runs.start with no extras.
 */
function QuickStartInline(): React.ReactElement | null {
  const agentsQuery = trpc.agents.list.useQuery({});
  const startMutation = trpc.runs.start.useMutation();
  const navigate = useNavigate();
  const [agentId, setAgentId] = useState<string>('');

  const agents = agentsQuery.data?.data ?? [];
  // Default the dropdown to the first agent once data arrives so the
  // operator can click Start in one shot without first opening the
  // picker. fn-2.x (CodeRabbit) — runs in useEffect so React isn't
  // queueing setState during render; otherwise the previous
  // setTimeout pattern triggered repeated timers on every re-render.
  useEffect(() => {
    if (agentId === '' && agents.length > 0) {
      const first = agents[0];
      if (first !== undefined) setAgentId(first.id);
    }
  }, [agentId, agents]);

  if (agentsQuery.isPending) {
    return (
      <span style={{ color: 'var(--sb-text-muted)', fontSize: '0.85rem' }}>Loading agents…</span>
    );
  }
  if (agents.length === 0) return null;

  const handleStart = async (): Promise<void> => {
    if (agentId === '') return;
    try {
      const res = await startMutation.mutateAsync({ agentId });
      navigate(`/runs/${encodeURIComponent(res.id)}`);
    } catch {
      // Surfaces via startMutation.error below.
    }
  };

  return (
    <div
      className="runs-list-quickstart"
      style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}
    >
      <label className="visually-hidden" htmlFor="runs-list-quickstart-agent">
        Agent
      </label>
      <select
        id="runs-list-quickstart-agent"
        value={agentId}
        onChange={(e) => setAgentId(e.target.value)}
        disabled={startMutation.isPending}
        style={{
          padding: '0.4rem 0.6rem',
          border: '1px solid var(--sb-border)',
          borderRadius: '6px',
          background: 'var(--sb-bg-panel)',
          fontSize: '0.85rem',
          maxWidth: '18rem',
        }}
      >
        {agents.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => void handleStart()}
        disabled={agentId === '' || startMutation.isPending}
        className="runs-list-quickstart-go"
        style={{
          padding: '0.4rem 0.85rem',
          border: '1px solid var(--sb-border-strong, var(--sb-border))',
          borderRadius: '6px',
          background: 'var(--sb-bg-button, var(--sb-bg-panel))',
          fontSize: '0.85rem',
          cursor: 'pointer',
        }}
      >
        {startMutation.isPending ? 'Starting…' : '+ Start session'}
      </button>
      {startMutation.error !== null && (
        <span
          role="alert"
          style={{ color: 'var(--sb-status-failed)', fontSize: '0.78rem', marginLeft: '0.4rem' }}
        >
          {startMutation.error.message}
        </span>
      )}
    </div>
  );
}
